/**
 * VoucherViewerModal — Modal de SOLO LECTURA del comprobante contable.
 * Pensado para abrirse desde reportes sin perder el contexto del reporte.
 * Para edición/aprobación/etc usar VoucherDetailModal en VouchersTab.
 */
import { Loader2, ExternalLink, FileDown } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { Link } from 'react-router-dom'
import { fmtDate } from '@/lib/utils'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import apiClient from '@/lib/apiClient'
import { useVoucher } from '../hooks/useAccounting'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { VOUCHER_STATUS } from '../../../components/ds/statusCatalogs'
import { Modal } from '@/components/ds/Modal'

const TYPE_LABELS: Record<string, string> = {
  sale:      'Venta',
  purchase:  'Compra',
  cash:      'Caja',
  bank:      'Banco',
  manual:    'Manual',
  reversal:  'Reversa',
  fy_close:  'Cierre',
}


const fmtCLP = (v: string | number | null | undefined): string => {
  if (v == null) return '—'
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (isNaN(n)) return '—'
  return formatMoneyForTenant(n)
}

interface Props {
  voucherId: string
  onClose: () => void
}

export function VoucherViewerModal({ voucherId, onClose }: Props) {
  const locale = useTenantLocale()
  const { data: voucher, isLoading } = useVoucher(voucherId)

  const handleDownloadPdf = async () => {
    if (!voucher) return
    try {
      const res = await apiClient.get(`/accounting/vouchers/${voucher.id}/pdf/`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      window.open(url, '_blank')
    } catch {
      // ignore
    }
  }


  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span>
          Comprobante
          {voucher?.number && <span className="ml-2 text-content-subtle font-mono text-sm">#{voucher.number}</span>}
        </span>
      }
      actions={
        <>
          {voucher && (
            <Link
              to={`/accounting/vouchers?highlight=${voucher.id}`}
              onClick={onClose}
              className="mr-auto inline-flex items-center gap-1 px-2 py-1 text-xs text-primary hover:text-primary-hover underline decoration-dotted"
              title="Abrir en la pantalla de comprobantes (para editar/aprobar/etc)"
            >
              <ExternalLink size={12} /> Abrir completo
            </Link>
          )}
          {voucher && (
            <button
              type="button"
              onClick={handleDownloadPdf}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft"
            >
              <FileDown size={13} /> PDF
            </button>
          )}
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-divider rounded-lg hover:bg-soft">
            Cerrar
          </button>
        </>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando…
        </div>
      ) : voucher ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-content-muted mb-0.5">Tipo</p>
              <p className="font-medium">{TYPE_LABELS[voucher.voucher_type] ?? voucher.voucher_type}</p>
            </div>
            <div>
              <p className="text-xs text-content-muted mb-0.5">Fecha</p>
              <p className="font-medium">{fmtDate(voucher.date, locale)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-content-muted mb-0.5">Descripción</p>
              <p className="font-medium">{voucher.description}</p>
            </div>
            <div>
              <p className="text-xs text-content-muted mb-0.5">Estado</p>
              {voucher && (
                <StatusBadge status={voucher.status} catalog={VOUCHER_STATUS} />
              )}
            </div>
            {voucher.currency && (
              <div>
                <p className="text-xs text-content-muted mb-0.5">Moneda</p>
                <p className="font-medium">{voucher.currency} {voucher.exchange_rate && <span className="text-xs text-content-subtle">(tasa {voucher.exchange_rate})</span>}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-content-muted uppercase mb-2">Líneas</p>
            <DataTable
              columns={[
                {
                  key: 'account', header: 'Cuenta',
                  render: line => <span className="font-mono text-xs text-content">{line.account_code} — {line.account_name}</span>,
                },
                { key: 'glosa', header: 'Glosa', render: line => line.glosa || '—' },
                {
                  key: 'debe', header: 'Debe', numeric: true,
                  render: line => (parseFloat(line.debe || '0') !== 0 ? fmtCLP(line.debe) : '—'),
                },
                {
                  key: 'haber', header: 'Haber', numeric: true,
                  render: line => (parseFloat(line.haber || '0') !== 0 ? fmtCLP(line.haber) : '—'),
                },
              ]}
              rows={voucher.lines}
              rowKey={(line, i) => line.id ?? i}
              footer={{ debe: fmtCLP(voucher.total_debe), haber: fmtCLP(voucher.total_haber) }}
              surface={false}
              minWidth={560}
              caption="Líneas del comprobante"
            />
          </div>
        </div>
      ) : (
        <div className="py-8 text-sm text-content-subtle text-center">No se pudo cargar el comprobante.</div>
      )}
    </Modal>
  )
}
